/* eslint-disable no-underscore-dangle, react/jsx-filename-extension */

import React from 'react';
import { hydrate } from 'react-dom';
import { BrowserRouter } from 'react-router-dom';

// Make sure this image is pulled into dist
import './assets_seller/og-website-image-4.5.png';

import { ConfigProvider } from 'Context/config';
import { UserProvider } from 'Context/user';
import { VehicleLookupProvider } from 'Context/vehicleLookup';
import { VehicleProvider } from 'Context/vehicles';

import { fastlaneFormatIntState } from 'Utilities/fastlane';
import { User } from 'Utilities/user/@types';

import App from '../shared/containers/app/App';
import Status500 from '../shared/containers/app/error/500';
import ErrorBoundary from '../shared/containers/app/error/ErrorBoundary';
import Checkout from '../shared/containers/checkout/Checkout';
import { StoreProvider } from '../shared/store';

import { InitialContext, InitialState } from './index.helper';

declare global {
  // eslint-disable-next-line vars-on-top, no-var
  var Sentry: {
    captureException: (error: unknown, sentryOpts?: unknown) => void;
  };
  interface Window {
    Sentry: {
      captureException: Function;
      captureMessage: Function;
      configureScope: Function;
    };
    __CONTAINER__: string;
    __CONTEXT__: InitialContext;
    __ERROR__?: boolean;
    __INITIAL_STATE__: InitialState;
    __SP_SESSION_TIME__: number;
    dataLayer: Record<string, any>[];
    kameleoonQueue: { push: (data: Array<string | number>) => void };
    mswStop?: Function;
    snowplow: Function;
  }
}

const init = () => {
  const { history } = window;

  // https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  if (!('IntersectionObserver' in window)) { // Stop <= iOS 12.1 from booting
    return;
  }

  const initialState = window.__INITIAL_STATE__;
  const container = window.__CONTAINER__;
  const { vehicles, ...user } = initialState?.user || {};
  const $app = document.getElementById('app');

  if (container === 'checkout') {
    const data = window.__CONTEXT__;
    hydrate(<Checkout {...{ ...data }} />, $app);
  } else if (container === 'error-page') {
    hydrate(<Status500 />, $app);
  } else if (container === 'total-error') {
    hydrate(<p>Sorry, the page encountered an error, please try refreshing</p>, $app);
  } else {
    // Do not use StoreProvider going forward!! Want to kill with 🔥🔥🔥🔥
    hydrate(
      (
        <StoreProvider initialState={initialState}>
          <ConfigProvider value={fastlaneFormatIntState(initialState)}>
            <UserProvider value={Object.keys(user).length ? user as User : null}>
              <VehicleProvider value={vehicles}>
                <VehicleLookupProvider value={vehicles && vehicles[0]}>
                  <BrowserRouter>
                    <ErrorBoundary>
                      <App hasError={window.__ERROR__} />
                    </ErrorBoundary>
                  </BrowserRouter>
                </VehicleLookupProvider>
              </VehicleProvider>
            </UserProvider>
          </ConfigProvider>
        </StoreProvider>
      ), $app,
    );
  }
};

init();
